//
//  useImportUser.ts
//
//  isana.net,inc. Proprietary and Confidential
//
//

import { useState, useCallback } from "react";
import { usePapaParse } from "react-papaparse";
import { postFitinsightUserList } from "../network/api";
import { FitinsightUser } from "../network/type";

const MESSAGES = {
  INVALID_CSV: "想定外の形式です",
  MISSING_HEADERS: "必要なヘッダーがありません",
  ERROR_REGISTER: "登録に失敗しました",
};

const REQUIRED_HEADERS = ["companyID", "ID"] as const;

type Result = {
  success: string[];
  failed: string[];
};

const decodeText = (buffer: ArrayBuffer): string | null => {
  const decoders = [
    new TextDecoder("shift-jis", { fatal: true }),
    new TextDecoder("utf-8", { fatal: true }),
  ];
  for (const decoder of decoders) {
    try {
      return decoder.decode(buffer);
    } catch {
      // continue
    }
  }
  return null;
};

export const useImportUser = () => {
  const [csvData, setCsvData] = useState<FitinsightUser[]>([]);
  const [result, setResult] = useState<Result>({ success: [], failed: [] });
  const [importResult, setImportResult] = useState<string | null>(null);
  const { readString } = usePapaParse();

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      setCsvData([]);
      setImportResult(null);
      setResult({ success: [], failed: [] });
      if (!file) return;
      const buffer = await file.arrayBuffer();
      const text = decodeText(buffer);
      if (!text) {
        setImportResult(MESSAGES.INVALID_CSV);
        return;
      }
      readString(text, {
        complete: (result) => {
          const rows = result.data as string[][];
          if (rows.length === 0 || rows[0].length === 0) {
            setImportResult(MESSAGES.INVALID_CSV);
            return;
          }
          const headers = rows[0];
          const missingHeaders = REQUIRED_HEADERS.filter(
            (header) => !headers.includes(header)
          );
          if (missingHeaders.length > 0) {
            setImportResult(
              `${MESSAGES.MISSING_HEADERS} ${missingHeaders.join(", ")}`
            );
            return;
          }
          const companyIdIndex = headers.indexOf(REQUIRED_HEADERS[0]);
          const userIdIndex = headers.indexOf(REQUIRED_HEADERS[1]);
          const users = rows
            .slice(1)
            .filter((row) => row[companyIdIndex] && row[userIdIndex])
            .map((row) => ({
              companyId: row[companyIdIndex],
              fitinsightUniqueId: row[userIdIndex],
              belongingGroup: "",
            }));
          setCsvData(users);
          setImportResult(null);
        },
      });
    },
    []
  );

  const submitUserData = useCallback(async () => {
    try {
      const response = await postFitinsightUserList(csvData);
      if (!response.isSuccessful) {
        const message =
          typeof response.isSuccessful === "string"
            ? `${MESSAGES.ERROR_REGISTER}: ${response.isSuccessful}`
            : MESSAGES.ERROR_REGISTER;
        setImportResult(message);
        return;
      }
      setCsvData([]);
      setResult({
        success: response.successUsers,
        failed: response.failedUsers,
      });
    } catch (error) {
      setImportResult(MESSAGES.ERROR_REGISTER);
    }
  }, [csvData]);

  return {
    csvData,
    result,
    importResult,
    handleFileChange,
    submitUserData,
  };
};
